
import React, { useRef, useEffect, useState } from 'react';
import mapboxgl, { Map } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { SiteAdminDetail, LocationAdminDetail } from 'src/api/Api';
import MapboxFeature from 'src/mapbox-helpers/MapboxFeature';
import { mapBoxConfig } from 'src/mapbox-helpers/config';
import { bbox } from '@turf/turf';

mapboxgl.accessToken = mapBoxConfig.accessToken;

interface SiteDetailMapProps {
  site: SiteAdminDetail;
  locations: LocationAdminDetail[];
  onLocationClick: (locationId: string) => void;
  selectedLocationId?: string;
}

const SiteDetailMap: React.FC<SiteDetailMapProps> = ({
  site,
  locations,
  onLocationClick,
  selectedLocationId,
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const [mapStyle] = useState<string>(mapBoxConfig.mapStyle);

  useEffect(() => {
    const addLocationMarkers = () => {
      locations.forEach((location) => {
        if (!location.location) return;
        const point = MapboxFeature.parseFeatureFromString(location.location);
        if (point?.geometry?.type === 'Point') {
          const coordinates = point.geometry.coordinates as [number, number];
          if (coordinates && mapRef.current) {
            const markerEl = document.createElement('div');
            markerEl.className = 'marker';
            markerEl.setAttribute('data-location-id', location.location_id);
            markerEl.style.backgroundImage = 'url(/maki_icons/svgs/marker.svg)';
            markerEl.style.width = '30px';
            markerEl.style.height = '30px';
            markerEl.style.backgroundSize = 'contain';
            markerEl.style.cursor = 'pointer';

            const marker = new mapboxgl.Marker(markerEl)
              .setLngLat(coordinates)
              .setPopup(new mapboxgl.Popup().setText(location.location_name))
              .addTo(mapRef.current);

            marker.getElement().addEventListener('click', () => {
              onLocationClick(location.location_id);
            });
          }
        }
      });
    };

    const fitMapToBoundaries = () => {
      if (!mapRef.current || !site.boundary) return;
      const tempFeature = new MapboxFeature();
      tempFeature.add(site.boundary, {});
      const geoJsonBoundary = tempFeature.toJSON();

      if (geoJsonBoundary && geoJsonBoundary.features.length > 0) {
        try {
          const calculatedBbox = bbox(geoJsonBoundary);
          mapRef.current.fitBounds(calculatedBbox as [number, number, number, number], {
            padding: 50,
            animate: true,
            duration: 1500,
          });
        } catch (e) {
          console.error("Error calculating bounding box for single site:", e);
        }
      }
    };

    const addSiteBoundary = () => {
      if (!site.boundary) return;
      const polygon = new MapboxFeature();
      const sourceId = site.site_id;
      polygon.add(site.boundary, {});
      mapRef.current?.addSource(sourceId, {
        type: 'geojson',
        data: polygon.toJSON(),
      });

      mapRef.current?.addLayer({
        id: `fill-${sourceId}`,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': mapBoxConfig.polygon.fillColor,
          'fill-opacity': 0.2,
        },
        filter: ['==', '$type', 'Polygon'],
      });
    };

    const onMapLoad = () => {
      if (!mapContainer.current || !site) return;
      addSiteBoundary();
      addLocationMarkers();
      fitMapToBoundaries();
    };

    const resizeMap = () => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    };

    if (!mapContainer.current) return;
    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current as HTMLDivElement,
      style: `mapbox://styles/mapbox/${mapStyle}`,
      center: [mapBoxConfig.defaulView.lng, mapBoxConfig.defaulView.lat],
    });

    mapRef.current?.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
    mapRef.current?.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');

    mapRef.current?.on('load', onMapLoad);
    window.addEventListener('resize', resizeMap);
    return () => {
      mapRef.current?.remove();
      window.removeEventListener('resize', resizeMap);
    };
  }, [mapStyle, site, locations]);

  useEffect(() => {
    if (mapRef.current) {
      const markers = document.getElementsByClassName('mapboxgl-marker');
      for (let i = 0; i < markers.length; i++) {
        const marker = markers[i] as HTMLElement;
        const markerLocationId = marker.getAttribute('data-location-id');
        if (markerLocationId === selectedLocationId) {
          marker.style.border = '2px solid red';
          marker.style.borderRadius = '50%';
        } else {
          marker.style.border = 'none';
        }
      }
    }
  }, [selectedLocationId]);

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <div ref={mapContainer} style={{ position: 'absolute', top: 0, bottom: 0, width: '100%' }} />
    </div>
  );
};


export default SiteDetailMap;
